<template>
    <div>
        <x-input :text="targetLabel" :value="targetLayerName" plaintext />
        <x-select
            v-if="
                adding && tableNamesOptions.length > 0 && !hasRelationTable
            "
            :id="`relationTable${relation.id || 'newRelationTable'}`"
            :name="'relationTable'"
            :text="`${this.$t('relations.joiningTable')}${this.$t(
                'labelOptional'
            )}`"
            :options="tableNamesOptions"
            @selected="$_onTableNameSelected"
        />
        <x-input
            v-if="hasRelationTable"
            :text="$t('relations.joiningTable')"
            :value="relationTableName"
            plaintext
        />
        <x-input
            :id="`relationName${relation.id || 'new'}`"
            :name="'relationName'"
            :text="$t('relations.name')"
            v-model="relationData.name"
            :state="!nameInUse"
            :invalidMessage="$t('tableResults.nameInUse')"
        />
        <b-form-group
            v-if="
                relationshipType != RELATIONSHIP_TYPES.ThemeLayer2Table &&
                relationshipType != RELATIONSHIP_TYPES.Table2Table
            "
            :label="$t('relations.type')"
            v-slot="{ ariaDescribedby }"
            :disabled="!adding"
        >
            <b-form-radio
                v-model="relationData.relationType"
                :aria-describedby="ariaDescribedby"
                name="some-radios"
                :value="RELATION_TYPES.Normal"
            >
                {{ $t('relations.typeNormal') }}
            </b-form-radio>
            <b-form-radio
                v-model="relationData.relationType"
                :aria-describedby="ariaDescribedby"
                name="some-radios"
                :value="RELATION_TYPES.Spatial"
            >
                {{ $t('relations.typeSpatial') }}
            </b-form-radio>
        </b-form-group>
        <div v-if="showSpinner">
            <label>{{ $t('attributesLoadingMessage') }}</label>
            <b-spinner small class="ml-1" label="Loading" />
        </div>
        <template
            v-if="
                relationData.relationType === RELATION_TYPES.Normal &&
                sourcePropertiesChoices &&
                targetPropertiesChoices
            "
        >
            <div
                v-for="(relationItem, index) in relationData.settings.items"
                :key="`relationItem_${index}`"
                class="accordion"
                role="tablist"
            >
                <b-card no-body class="mb-1">
                    <b-card-header
                        header-tag="header"
                        :class="{
                            'p-1': true,
                            collapsed: !accordionState[index],
                            'not-collapsed': accordionState[index]
                        }"
                        role="tab"
                        @click="$_selectRelation(index)"
                        data-toggle="collapse"
                    >
                        <span>{{
                            relationItem.sourceFieldName
                                ? relationItem.sourceFieldName.toUpperCase()
                                : ''
                        }}</span>
                        <div class="float-right">
                            <a
                                :title="$t('relations.deleteAttribute')"
                                class="pr-4"
                                @click.stop.prevent="
                                    $_onRemoveRelationItem(index)
                                "
                            >
                                <em class="fas fa-trash-alt fa-sm"></em>
                            </a>
                            <em class="fa iconCollapseExpand"></em>
                        </div>
                    </b-card-header>
                    <b-collapse
                        :id="`relationItem-${index}`"
                        v-model="accordionState[index]"
                        role="tabpanel"
                    >
                        <b-card-body v-if="accordionState[index] === true">
                            <span
                                v-if="hasRelationTable"
                                class="font-weight-bold"
                                >{{ $t('tableResults.titleSingular') }}
                                <img
                                    src="~@/assets/imported-icons/ic_fluent_arrow_right_20_regular.svg"
                                    :alt="$t('tableResults.titleSingular')"
                                />
                                {{ $t('relations.joiningTable') }}
                            </span>
                            <relation-item
                                :class="hasRelationTable ? 'ml-3' : ''"
                                :relationItem="relationItem"
                                :sourcePropertiesChoices="
                                    sourcePropertiesChoices
                                "
                                :targetPropertiesChoices="
                                    targetPropertiesChoices
                                "
                                @update="
                                    $_onUpdateRelationItem(index, $event)
                                "
                            />
                            <!--joining table-->
                            <span
                                v-if="hasRelationTable"
                                class="mt-3 d-block font-weight-bold"
                                >{{ $t('relations.joiningTable') }}
                                <img
                                    src="~@/assets/imported-icons/ic_fluent_arrow_right_20_regular.svg"
                                    :alt="$t('relations.joiningTable')"
                                />
                                {{ $t('tableResults.titleSingular') }}
                            </span>
                            <relation-item
                                :class="hasRelationTable ? 'ml-3' : ''"
                                v-if="hasRelationTable"
                                :relationItem="secondRelationItem(index)"
                                :sourcePropertiesChoices="
                                    sourcePropertiesChoicesJoiningTable
                                "
                                :targetPropertiesChoices="
                                    targetPropertiesChoicesJoiningTable
                                "
                                @update="
                                    $_onUpdateRelationItem(
                                        index,
                                        $event,
                                        true
                                    )
                                "
                            />
                        </b-card-body>
                    </b-collapse>
                </b-card>
            </div>
            <b-link
                :id="`addAttribute`"
                :name="`addAttribute`"
                @click="$_onAddRelationItem"
            >
                <em class="fa fa-plus pr-1"></em
                >{{ $t('relations.addAttribute') }}
            </b-link>
        </template>
        <div class="pt-3">
            <b-button
                v-if="!edit"
                variant="primary"
                :disabled="!valid"
                @click="$_updateRelation"
                class="align-items-center"
            >
                {{ adding ? $t('relations.add') : $t('relations.edit') }}
                <span class="fas fa-check"></span>
            </b-button>
            <b-button
                v-if="!edit"
                @click="$_cancelAdd"
                class="align-items-center"
            >
                {{ $t('btnCancelLabel') }}
                <span class="fas fa-times"></span>
            </b-button>
        </div>
    </div>
</template>

<script>
    import {
        RELATION_TYPES,
        RELATION_FIELD_DATA_TYPES,
        RELATIONSHIP_TYPES
    } from '../../../constants';
    import {
        arraySortAscendingFunction,
        convertToValueTextPair
    } from '../../../services/utilities';
    import modalMixin from '../../../components/Mixins/ModalMixin.vue';
    import RelationItem from './RelationItem.vue';
    import XInput from '../../BaseComponents/x-input.vue';
    import XSelect from '../../BaseComponents/x-select.vue';

    const relationSettingsTemplate = {
        sourceFieldName: null,
        destinationFieldName: null,
        dataType: RELATION_FIELD_DATA_TYPES.string,
        caseSensitive: true
    };

    export default {
        mixins: [modalMixin],
        props: {
            targetLabel: { type: String, required: true },
            relationshipType: { type: Number, required: true },
            relationList: { type: Array, required: true },
            relation: { type: Object, required: true },
            selectableLayers: { type: Array, required: true },
            tableNames: { type: Array, default: () => [] },
            sourceLayerId: { type: Number, required: true },
            edit: { type: Boolean, default: false }
        },
        created() {
            this.init();
        },
        components: {
            'relation-item': RelationItem,
            'x-input': XInput,
            'x-select': XSelect
        },
        data() {
            return {
                RELATIONSHIP_TYPES: RELATIONSHIP_TYPES,
                RELATION_TYPES: RELATION_TYPES,
                RELATION_FIELD_DATA_TYPES: RELATION_FIELD_DATA_TYPES,
                relationData: {
                    settings: {
                        relationTable: '',
                        items: [relationSettingsTemplate]
                    },
                    relationType: RELATION_TYPES.Normal,
                    ...this.relation
                },
                targetProperties: null,
                sourceProperties: null,
                targetPropertiesJoiningTable: null,
                sourcePropertiesJoiningTable: null,
                accordionState: []
            };
        },
        watch: {
            relationData: {
                handler: function () {
                    if (this.edit) {
                        this.$_updateRelation();
                    }
                },
                deep: true
            },
            'relationData.relationType': {
                handler: async function (newVal) {
                    if (
                        this.relationshipType ===
                            RELATIONSHIP_TYPES.ThemeLayer2Table ||
                        this.relationshipType === RELATIONSHIP_TYPES.Table2Table
                    ) {
                        if (!this.targetProperties) {
                            if (this.hasRelationTable) {
                                await this.$_fillSourceAndTargetProperties(
                                    this.relationTableName
                                );
                            } else {
                                this.targetProperties =
                                    await this.$_getTableResultProperties({
                                        id: this.relation.targetThemeLayerId
                                    });
                            }
                        }

                        // Source is a themelayer
                        if (
                            this.relationshipType ===
                            RELATIONSHIP_TYPES.ThemeLayer2Table
                        ) {
                            if (!this.sourceProperties) {
                                this.sourceProperties =
                                    await this.$_getLayerProperties(
                                        this.sourceLayerId
                                    );
                            }
                        }

                        // source is a table
                        if (
                            this.relationshipType === RELATIONSHIP_TYPES.Table2Table
                        ) {
                            if (!this.sourceProperties) {
                                this.sourceProperties =
                                    await this.$_getTableResultProperties({
                                        id: this.sourceLayerId
                                    });
                            }
                        }
                    }
                    if (
                        this.relationshipType ==
                            RELATIONSHIP_TYPES.ThemeLayer2ThemeLayer &&
                        newVal === RELATION_TYPES.Normal
                    ) {
                        if (!this.targetProperties) {
                            this.targetProperties = await this.$_getLayerProperties(
                                this.relation.targetThemeLayerId
                            );
                        }
                        if (!this.sourceProperties) {
                            this.sourceProperties = await this.$_getLayerProperties(
                                this.sourceLayerId
                            );
                        }
                    }
                },
                immediate: true
            }
        },
        computed: {
            adding() {
                return this.relation.id ? false : true;
            },
            targetLayerData() {
                return this.selectableLayers.find(
                    ({ id }) => this.relation.targetThemeLayerId === id
                );
            },
            targetLayerName() {
                return this.targetLayerData?.title;
            },
            sourcePropertiesChoices() {
                return this.sourceProperties?.map((property) => ({
                    text: property.name,
                    value: property
                }));
            },
            targetPropertiesChoices() {
                return this.targetProperties?.map((property) => ({
                    text: property.name,
                    value: property
                }));
            },
            sourcePropertiesChoicesJoiningTable() {
                return this.sourcePropertiesJoiningTable?.map((property) => ({
                    text: property.name,
                    value: property
                }));
            },
            targetPropertiesChoicesJoiningTable() {
                return this.targetPropertiesJoiningTable?.map((property) => ({
                    text: property.name,
                    value: property
                }));
            },
            tableNamesOptions() {
                return convertToValueTextPair(
                    this.tableNames.map(({ layerName }) => layerName)
                ).sort(arraySortAscendingFunction('text'));
            },
            nameInUse() {
                if (
                    (this.relationshipType ===
                        RELATIONSHIP_TYPES.ThemeLayer2Table ||
                        this.relationshipType === RELATIONSHIP_TYPES.Table2Table) &&
                    this.relationData
                ) {
                    return !!this.relationList.find(
                        (relation) =>
                            relation.id !== this.relationData.id &&
                            relation.name.toLowerCase() ===
                                this.relationData.name.toLowerCase()
                    );
                }
                return false;
            },
            valid() {
                const minFieldLength = 1;
                const hasName = this.relationData?.name?.length > minFieldLength;
                const hasValidName = !this.nameInUse;
                const hasType = this.relationData?.relationType !== null;
                let validMeta = true;
                let validMetaJoiningTable = true;
                if (this.relationData?.relationType === RELATION_TYPES.Normal) {
                    this.relationData?.settings?.items?.forEach((relationItem) => {
                        const hasSourceField =
                            relationItem.sourceFieldName?.length > minFieldLength;
                        const hasDestinationField =
                            relationItem.destinationFieldName?.length >
                            minFieldLength;
                        validMeta = hasSourceField && hasDestinationField;
                    });
                    // joining table
                    if (this.hasRelationTable) {
                        this.relationData?.settings?.itemsSecondRelation?.forEach(
                            (secondRelationItem) => {
                                const hasSourceField =
                                    secondRelationItem.sourceFieldName?.length >
                                    minFieldLength;
                                const hasDestinationField =
                                    secondRelationItem.destinationFieldName
                                        ?.length > minFieldLength;
                                validMetaJoiningTable =
                                    hasSourceField && hasDestinationField;
                            }
                        );
                    }
                }
                return (
                    hasName &&
                    hasValidName &&
                    hasType &&
                    validMeta &&
                    validMetaJoiningTable
                );
            },
            secondRelationItem() {
                return (index) =>
                    this.relationData.settings.itemsSecondRelation[index];
            },
            relationTableName() {
                return this.relationData.settings.relationTable;
            },
            hasRelationTable() {
                return !!this.relationData.settings.relationTable;
            },
            showSpinner() {
                return (
                    this.relationData.relationType === RELATION_TYPES.Normal &&
                    (!this.sourcePropertiesChoices || !this.targetPropertiesChoices)
                );
            }
        },
        methods: {
            async init() {
                if (this.adding) {
                    this.accordionState.push(true);
                    this.$set(this.relationData, 'name', this.targetLayerName);
                } else {
                    this.accordionState = this.relationData.settings?.items?.map(
                        () => false
                    );
                }
            },
            async $_getLayerProperties(id) {
                const layerDetails = await this.$store.dispatch('useApiData', {
                    name: 'GetThemeLayerDetails',
                    parameters: { id }
                });
                if (layerDetails?.data) {
                    const featuretypeinfo = await this.$store.dispatch(
                        'useApiData',
                        {
                            name: 'GetFeatureTypeInfoByName',
                            parameters: {
                                layerName: layerDetails.data.layerName,
                                owsId: layerDetails.data.sourceId
                            }
                        }
                    );
                    return featuretypeinfo?.data?.attributes
                        .filter(({ isGeometry }) => !isGeometry)
                        .map(({ name, type }) => ({
                            name,
                            type: type.replace('xsd:', '')
                        }));
                }
                return [];
            },
            async $_getTableResultProperties({ id, tableName }) {
                if (!tableName) {
                    const tableResult = await this.$store.dispatch('useApiData', {
                        name: 'GetTableSearchResult',
                        parameters: { Id: id }
                    });
                    tableName = tableResult?.data?.tableName;
                }
                if (tableName) {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetTableAttributes',
                        parameters: { TableName: tableName }
                    });
                    return result.data.map(({ name, binding }) => ({
                        name,
                        type: binding.replace('java.lang.', '').toLowerCase()
                    }));
                }
                return [];
            },
            $_updateRelation() {
                const relationData = { ...this.relationData };
                if (relationData.relationType === RELATION_TYPES.Spatial) {
                    relationData.settings = {};
                }
                this.$emit('update', relationData);
            },
            $_onAddRelationItem() {
                this.accordionState = this.relationData.settings?.items?.map(
                    () => false
                );
                const items = [
                    ...this.relationData.settings.items,
                    relationSettingsTemplate
                ];
                this.$set(this.relationData.settings, 'items', items);

                // joining table
                if (this.hasRelationTable) {
                    const itemsSecondRelation = [
                        ...this.relationData.settings.itemsSecondRelation,
                        relationSettingsTemplate
                    ];
                    this.$set(
                        this.relationData.settings,
                        'itemsSecondRelation',
                        itemsSecondRelation
                    );
                }
                this.accordionState.push(true);
            },
            $_onUpdateRelationItem(index, data, isSecondRelationItem) {
                if (isSecondRelationItem) {
                    // joining table
                    this.$set(
                        this.relationData.settings.itemsSecondRelation,
                        index,
                        data
                    );
                } else {
                    this.$set(this.relationData.settings.items, index, data);
                }
            },
            $_onRemoveRelationItem(index) {
                const data = {
                    message: this.$t('relations.deleteAttributeConfirmation')
                };
                this.msgBoxConfirm(data).then((confirmed) => {
                    if (confirmed) {
                        const items =
                            this.relationData?.settings?.items?.filter(
                                (__, _index) => _index !== index
                            ) || [];
                        this.$set(this.relationData.settings, 'items', items);
                    }
                });
            },
            $_cancelAdd() {
                this.$emit('cancel');
            },
            $_selectRelation(index) {
                const newVal = !this.accordionState[index];
                this.accordionState = this.relationData.settings.items.map(
                    (format) => false
                );
                this.$set(this.accordionState, index, newVal);
            },
            async $_onTableNameSelected(tableName) {
                if (tableName) {
                    this.$set(this.relationData.settings, 'itemsSecondRelation', [
                        relationSettingsTemplate
                    ]);
                    await this.$_fillSourceAndTargetProperties(tableName);
                }
                this.$set(
                    this.relationData.settings,
                    'relationTable',
                    tableName ?? undefined
                );
            },
            async $_fillSourceAndTargetProperties(tableName) {
                // joining table items
                this.targetProperties = await this.$_getTableResultProperties({
                    tableName
                });

                // joining table itemsSecondRelation
                this.sourcePropertiesJoiningTable = [...this.targetProperties];

                // table result
                this.targetPropertiesJoiningTable =
                    await this.$_getTableResultProperties({
                        id: this.relation.targetThemeLayerId
                    });
            }
        }
    };
</script>
